import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useRsvpAttendanceStatus } from '@shared/hooks/useRsvpAttendanceStatus';

export type UseShouldShowPostPurchaseBookingLinksArgs = Readonly<{
  eventHandle: string | undefined | null;
  /** Set when the caller already knows the CTA cannot render, to skip both reads. */
  skip?: boolean;
}>;

export type UseShouldShowPostPurchaseBookingLinksReturn = Readonly<{
  /** Whether the "Book your stay" CTA may render. False while still deciding. */
  shouldShowBookingLinks: boolean;
  /**
   * True while the answer is still unknown. Callers that only add or omit the CTA can
   * ignore this; callers that render a *different* layout depending on it should wait,
   * so the page doesn't rearrange itself once the status arrives.
   */
  isLoading: boolean;
}>;

/**
 * Whether a registry purchase confirmation may show its "Book your stay" CTA.
 *
 * Two independent gates, and every post-purchase surface needs both:
 * - `showBookingLinksOnRegistryPurchaseConfirmation`, the couple's opt-out in
 *   admin under Settings > Where to Stay.
 * - The guest's RSVP status — a guest who declined is not shown a hotel upsell.
 *
 * Returns false until the RSVP status resolves, so the CTA never flashes in front
 * of a declining guest. Guests whose status is unknown or unanswered still see it.
 */
export const useShouldShowPostPurchaseBookingLinks = ({ eventHandle, skip }: UseShouldShowPostPurchaseBookingLinksArgs): UseShouldShowPostPurchaseBookingLinksReturn => {
  const [isBookingLinksEnabled, isBookingLinksEnabledLoading] = useIsFeatureEnabled({
    consentKey: 'showBookingLinksOnRegistryPurchaseConfirmation',
  });

  const { isResolved: isRsvpStatusResolved, isNotAttending } = useRsvpAttendanceStatus({
    eventHandle,
    skip: skip || !isBookingLinksEnabled,
  });

  return {
    // A skipped caller has already decided the CTA cannot render, so say so rather than
    // reporting the flag's answer to a question nobody asked.
    shouldShowBookingLinks: !skip && isBookingLinksEnabled && !isBookingLinksEnabledLoading && isRsvpStatusResolved && !isNotAttending,
    // Distinct from `false`: a caller that swaps layout on the answer needs to hold, not
    // commit to the hidden variant and then rearrange once the status lands.
    isLoading: !skip && (isBookingLinksEnabledLoading || (isBookingLinksEnabled && !isRsvpStatusResolved)),
  };
};
